import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../components/introduction/introduction.component';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { EopAssistantComponent } from '../components/eop-assistant/eop-assistant.component';
import { PrvNextButtonsComponent } from '../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../shared/helpers/icons';
import { IUserInfo } from '../../shared/models/contact';
import { IEopDocument } from '../../shared/models/eopDocument';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../shared/services/userData.service';
import { EopDocumentService } from '../../shared/services/eopDocument.service';
import { EopSectionService } from '../../shared/services/eopSection.service';
import { ESectionRoutes, ESubSectionTypes, getSectionNameFromRoute } from '../../shared/models/eop_types.enum';
import { getPlanSlugFromUrl } from '../eop-config/eop-page-config';

@Component({
  selector: 'general-eop-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    EopAssistantComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './general-eop-page.component.html',
  styleUrl: './general-eop-page.component.css'
})
export class GeneralEopPageComponent {
  
  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  currentPageRoute: string | null = null;
  eopDoc: IEopDocument | null = null;
  eopSectionTypeId: number | null = null;
  eopSubSectionTypeId: number | null = null;
  pageTitle: string = '';
  description: string = '';
  
  subSectionsMap: { [key: string]: string[] } = {
    'evacuation': [
      'Evacuation',
      'Evacuation - Reunification',
      'Evacuation - Recovery'
    ],
    'lockdown': [
      'Lockdown',
      'Evacuation - Reunification',
      'Evacuation - Recovery'
    ],
    'secure-perimeter': [
      'Secure Perimeter',
      'Secure Perimeter - Reunification',
      'Secure Perimeter - Recovery'
    ],
    'shelter-in-place': [
      'Shelter In Place',
      'Shelter In Place - Reunification',
      'Shelter In Place - Recovery'
    ],
    'central-office-eop': [
      'Central Office Staffs Support to Schools',
      'Managing an Event at the Central Office',
      'Recovery',
      'School to School Support'
    ]
  };
  
  documents: string[] = [];
  selectedDoc: string | null = null;
  
  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userDataService: UserDataService,
    private eopDocumentService: EopDocumentService,
    private eopSeectionService: EopSectionService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.route.paramMap.subscribe(params => {
      this.currentPageRoute = params.get('pageRoute');
      if (this.currentPageRoute) {
        this.pageTitle = getSectionNameFromRoute(this.currentPageRoute)?.toUpperCase() || 'N/A';
        this.eopSectionTypeId = ESectionRoutes[this.currentPageRoute as keyof typeof ESectionRoutes];
        // Load subsections if needed
        const subSections = this.subSectionsMap[this.currentPageRoute];
        if (subSections) {
          this.documents = subSections;
          this.selectedDoc = subSections[0]; // Default to first subsection
          this.eopSubSectionTypeId = ESubSectionTypes[this.selectedDoc as keyof typeof ESubSectionTypes];
        } else {
          this.documents = []; 
          this.selectedDoc = null;
          this.eopSubSectionTypeId = null;
        }
        this.loadEopDoc();
      }
    });
  }

  getCurrentRoute(): string | null {
    const url = this.router.url.replace(/\/+$/, ''); // remove trailing slash
    const segments = url.split('/');
    return segments[-1] || segments[segments.length - 1]; // get last segment
  }

  loadEopDoc(): void {
    if (this.currentPageRoute == 'training-exercises' ||
      this.eopSubSectionTypeId == ESubSectionTypes['Record of Changes'] ||
      this.eopSubSectionTypeId == ESubSectionTypes['Record of Distribution']
    ) {
      return;
    }
    if (this.currentBuildingId && this.currentPageRoute && this.eopSectionTypeId) {
      this.loading = true;
      this.eopDocumentService.getEopDocument(
        this.currentBuildingId, 
        this.eopSectionTypeId,
        this.eopSubSectionTypeId ?? undefined
      ).subscribe({
        next: (response: any) => {
          if (!response.has_error) {
            this.eopDoc = response.data ? response.data : null;
            console.table(this.eopDoc);
            this.description = this.eopDoc?.description || '';
          }
          this.loading = false;
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error:', error.message);
        }
      })
    } else return;
  }

  onSubmit(): void {
    if (!this.description.trim()) {
      return;
    }
    if (this.currentBuildingId && this.currentPageRoute && this.eopSectionTypeId) {
      const updateData = { description: this.description };
    
      this.loading = true;
      this.eopDocumentService.updateEopDocument(
        this.currentBuildingId, 
        updateData,
        this.eopSectionTypeId,
        this.eopSubSectionTypeId ?? undefined
      ).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating an eop document.';
            this.loadEopDoc();
          }
        },
        error: (error) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });

    }
  }
  

  handleApproval(): void {
    if (this.currentBuildingId && this.currentPageRoute && this.eopSectionTypeId) {
      this.loading = true;
      this.eopDocumentService.approveEopDocument(
        this.currentBuildingId,
        this.eopSectionTypeId,
        this.eopSubSectionTypeId ?? undefined
      ).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after confirming an eop document.';
            this.loadEopDoc();
          } else {
            this.messageModal = 'unsuccess';
            this.popupMessage = response.message || 'An error occurred while confirming the document.';
          }
        },
        error: (error) => {
          this.loading = false;
          this.messageModal = "unsuccess";
          this.popupMessage = error.message;
        }
      })
    }
  }

  selectDoc(doc: string) {
    this.selectedDoc = doc;
    this.eopSubSectionTypeId = ESubSectionTypes[doc as keyof typeof ESubSectionTypes];
    this.loadEopDoc();
  }
  

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  get isContentUnchanged(): boolean {
    return this.description.trim() === this.eopDoc?.description?.trim();
  }

  get isGaPlan(): boolean {
    return getPlanSlugFromUrl(this.router.url) === 'ga';
  }

}
